/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Icon, Form, Message, Checkbox as CheckboxBase } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { isEmpty, sortBy, map, property, indexOf, filter, get } from 'lodash';

import { Modal, Button, Table } from '../../../../components';

import type { Master } from '../../../../type';

const intlMessages = defineMessages({
    notExists: {
        id: 'pages.clients.masterList.error.notExists',
        defaultMessage: 'This client does not exist',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

export const GET_DEFAULT_MASTER_LIST = gql`
    query getDefaultMasters($salon: ID!, $client: ID!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    masters {
                        nodes {
                            id
                            firstName
                            lastName
                        }
                    }
                    client(id: $client) {
                        id
                        defaultMasters {
                            id
                        }
                    }
                }
            }
        }
    }
`;

export const SET_DEFAULT_MASTER_LIST = gql`
    mutation setDefaultMasters($input: SetDefaultMastersInput!) {
        setDefaultMasters(input: $input) {
            client {
                id
                defaultMasters {
                    id
                }
            }
        }
    }
`;

const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={2} textAlign="center">
            <FormattedMessage
                id="pages.clients.masterList.table.empty"
                defaultMessage="There is no masters in this salon"
            />
        </Table.Cell>
    </Table.Row>
);

const CheckBoxCell = styled(Table.Cell)`
    .ui.table td& {
        text-align: center;
    }
`;

type MasterRowProps = {
    master: Master,
    index: number,
    control: Object,
    readonly: boolean,
};

const MasterRow = ({ master, index, control, readonly }: MasterRowProps) => (
    <Table.Row>
        <CheckBoxCell width={1}>
            <Controller
                name={`masters.${index}`}
                control={control}
                render={({ field: { value, onChange } }) => (
                    <CheckboxBase
                        checked={!!value}
                        onChange={(e, data) => onChange(data.checked)}
                        disabled={readonly}
                        type="checkbox"
                    />
                )}
            />
        </CheckBoxCell>
        <Table.Cell>
            {`${master.firstName} ${master.lastName || ''}`}
        </Table.Cell>
    </Table.Row>
);

/**
 * Self-contained MasterList: fetches its own data via useQuery/useMutation and
 * owns its form state via react-hook-form. The `masters` field is an array of
 * booleans mirroring the master list order.
 */
const MasterList = ({ client, onClose, intl, readonly }) => {
    const salon = useSelector(({ user }) => user.get('salon'));

    const { data } = useQuery(GET_DEFAULT_MASTER_LIST, {
        variables: { salon, client: client.id },
    });

    const [setDefaultMasters] = useMutation(SET_DEFAULT_MASTER_LIST);

    // Compute masters list with checked state (mirrors old withProps + withData logic)
    const rawMasters = sortBy(
        filter(get(data, 'viewer.salon.masters.nodes', []), Boolean),
        ({ firstName }) => String(firstName).toLowerCase(),
    );
    const defaultMasterIds = map(
        get(data, 'viewer.salon.client.defaultMasters', []),
        property('id'),
    );
    const masters = map(rawMasters, master =>
        Object.assign(
            { checked: !!master && indexOf(defaultMasterIds, master.id) !== -1 },
            master,
        ),
    );

    const {
        control,
        handleSubmit,
        reset,
        setError,
        formState: { isSubmitting, isDirty, errors },
    } = useForm({
        defaultValues: { masters: map(masters, property('checked')) },
    });

    // enableReinitialize equivalent: re-seed the form once query data arrives.
    useEffect(() => {
        reset({ masters: map(masters, property('checked')) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const onSubmit = async formData => {
        const selectedMasters = filter(
            map(formData.masters, (v, i) => (v ? masters[i].id : null)),
            Boolean,
        );

        try {
            await setDefaultMasters({
                variables: {
                    input: { client: client.id, salon, masters: selectedMasters },
                },
            });
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (
                graphQLError &&
                graphQLError.data &&
                get(graphQLError, 'data.error') === 'CLIENT_NOT_EXISTS'
            ) {
                setError('root', {
                    message: intl.formatMessage(intlMessages.notExists),
                });
                return;
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <Modal.Context>
            <Modal.Content>
                <Form error={!!errors.root}>
                    <Table celled striped selectable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell width={1} />
                                <Table.HeaderCell>
                                    <FormattedMessage
                                        id="pages.clients.masterList.table.master"
                                        defaultMessage="Master"
                                    />
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {isEmpty(masters) ? (
                                <EmptyMessage />
                            ) : (
                                masters.map((master, idx) => (
                                    <MasterRow
                                        key={master.id}
                                        master={master}
                                        index={idx}
                                        control={control}
                                        readonly={readonly}
                                    />
                                ))
                            )}
                        </Table.Body>
                    </Table>
                    {errors.root && (
                        <Message error content={errors.root.message} />
                    )}
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting || !isDirty}
                    loading={isSubmitting}
                    primary
                >
                    <FormattedMessage
                        id="pages.clients.masterList.button.save"
                        defaultMessage="Save"
                    />
                </Button>
                <Button onClick={onClose} disabled={isSubmitting} basic>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.clients.masterList.button.close"
                        defaultMessage="Cancel"
                    />
                </Button>
            </Modal.Actions>
        </Modal.Context>
    );
};

export default injectIntl(MasterList);
